import { Icon20Add } from '@vkontakte/icons';
import { Button } from '@vkontakte/vkui';
import React, { type ReactElement, memo, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { familyAddButtonMaxMembers } from 'reactApp/appHelpers/featuresHelpers/features/familyFunctions';
import { getFamilyMemberList, isUserFamilyOwner } from 'reactApp/modules/family/family.selectors';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { getIdByStorage, getStorage } from 'reactApp/modules/storage/storage.helpers';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { Avatar } from 'reactApp/ui/Avatar/Avatar';
import { sendDwh } from 'reactApp/utils/ga';

import styles from './FamilyAddButton.css';

export const FamilyAddButton = memo((): ReactElement | null => {
    const navigate = useNavigate();
    const email = useSelector(UserSelectors.getEmail);
    const isFamilyOwner = useSelector(isUserFamilyOwner);
    const familyMembers = useSelector(getFamilyMemberList);
    const memberCount = familyMembers.length;
    const currentStorage = useSelector(getCurrentStorage);

    const { isHome, isGallery, isAlbums, isDocuments, isFamilyFolders, isFeed, isSharedLinks, isFavorites, isTrashBin, isAttaches } =
        getStorage(currentStorage);

    const isAllowedSection =
        isHome ||
        isGallery ||
        isAlbums ||
        isDocuments ||
        isFamilyFolders ||
        isFeed ||
        isSharedLinks ||
        isFavorites ||
        isTrashBin ||
        isAttaches;

    const isBannerVisible = useMemo(
        () => isAllowedSection && isFamilyOwner && memberCount < familyAddButtonMaxMembers,
        [isAllowedSection, isFamilyOwner, memberCount]
    );

    useEffect(() => {
        if (isBannerVisible) {
            sendDwh({
                eventCategory: 'family-banner',
                action: 'show',
                p: 'cloud-web-analytics',
                dwh_enabled: true,
            });
        }
    }, [isBannerVisible]);

    const handleClick = () => {
        sendDwh({
            eventCategory: 'family-banner',
            action: 'click',
            p: 'cloud-web-analytics',
            dwh_enabled: true,
        });
        navigate(getIdByStorage(EStorageType.family));
    };

    if (!isBannerVisible) {
        return null;
    }

    return (
        <>
            <div>
                <Button
                    data-qa-id="family-add-button"
                    className={styles.button}
                    size="l"
                    align="left"
                    mode="primary"
                    appearance="accent"
                    onClick={handleClick}
                    after={
                        <div className={styles.avatars}>
                            {email && (
                                <div className={styles.avatarImage}>
                                    <Avatar email={email} size={25} />
                                </div>
                            )}
                            <div className={styles.addAvatar}>
                                <Icon20Add width={18} height={18} />
                            </div>
                        </div>
                    }
                >
                    Добавить близких
                </Button>
            </div>
        </>
    );
});

FamilyAddButton.displayName = 'FamilyAddButton';
